import React, { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { api, euro, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash, ArrowsClockwise, Pencil } from "@phosphor-icons/react";
import MoneyInput from "../components/MoneyInput";

export default function Providers() {
    const { agencyId, user } = useAuth();
    const [providers, setProviders] = useState([]);
    const [wallets, setWallets] = useState([]);
    const [open, setOpen] = useState(false);
    const [syncOpen, setSyncOpen] = useState(null);
    const [editOpen, setEditOpen] = useState(null);
    const [editForm, setEditForm] = useState({ name: "", fido_limit: "" });
    const [confirmDel, setConfirmDel] = useState(null);
    const [syncForm, setSyncForm] = useState({ residuo: "", wallet_cash_id: "" });
    const [form, setForm] = useState({ name: "", fido_limit: "" });
    const canEdit = ["owner", "agency_manager"].includes(user?.role);

    const load = () => {
        if (!agencyId) return;
        api.get(`/providers`, { params: { agency_id: agencyId } }).then((r) =>
            setProviders(r.data),
        );
        api.get(`/wallets`, { params: { agency_id: agencyId } }).then((r) =>
            setWallets(r.data),
        );
    };
    useEffect(() => {
        load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [agencyId]);

    const cashWallets = wallets.filter((w) =>
        ["cassa", "cassaforte", "banca"].includes(w.type),
    );

    const create = async (e) => {
        e.preventDefault();
        try {
            await api.post("/providers", {
                name: form.name,
                fido_limit: parseFloat(form.fido_limit) || 0,
                agency_id: agencyId,
            });
            toast.success("Fornitore aggiunto");
            setOpen(false);
            setForm({ name: "", fido_limit: "" });
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const remove = async () => {
        if (!confirmDel) return;
        try {
            await api.delete(`/providers/${confirmDel.id}`);
            toast.success("Fornitore eliminato");
            setConfirmDel(null);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const openEdit = (p) => {
        setEditForm({ name: p.name, fido_limit: String(p.fido_limit ?? "") });
        setEditOpen(p);
    };

    const saveEdit = async (e) => {
        e.preventDefault();
        try {
            await api.patch(`/providers/${editOpen.id}`, {
                name: editForm.name.trim(),
                fido_limit: parseFloat(editForm.fido_limit) || 0,
            });
            toast.success("Fornitore aggiornato");
            setEditOpen(null);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const sync = async (e) => {
        e.preventDefault();
        try {
            const r = await api.post("/providers/sync-residuo", {
                provider_id: syncOpen.id,
                residuo: parseFloat(syncForm.residuo),
                wallet_cash_id: syncForm.wallet_cash_id || null,
            });
            const { delta, tipo } = r.data;
            if (Math.abs(delta) < 0.01) {
                toast.info("Residuo già allineato, nessun movimento creato");
            } else if (tipo === "vendita_credito") {
                toast.success(`Registrati ${euro(delta)} di vendita credito in cassa`);
            } else if (tipo === "vincita_pagata") {
                toast.success(
                    `Registrata vincita pagata di ${euro(Math.abs(delta))} dalla cassa`,
                );
            }
            setSyncOpen(null);
            setSyncForm({ residuo: "", wallet_cash_id: "" });
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    return (
        <div data-testid="providers-page">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
                <div>
                    <div className="overline mb-2">Credito Fornitori</div>
                    <h1 className="text-4xl md:text-5xl font-black tracking-tighter">
                        Fido
                    </h1>
                </div>
                {canEdit && (
                    <button
                        onClick={() => setOpen(true)}
                        className="btn-primary flex items-center gap-2"
                        data-testid="add-provider-btn"
                    >
                        <Plus size={14} weight="bold" /> Nuovo fornitore
                    </button>
                )}
            </div>

            <div className="bg-white border border-zinc-200 overflow-x-auto">
                <table className="ft-table">
                    <thead>
                        <tr>
                            <th>Fornitore</th>
                            <th className="text-right">Fido / Tipo</th>
                            <th className="text-right">Esposizione</th>
                            <th className="text-right">Residuo / Prepagato</th>
                            <th>Utilizzo</th>
                            <th className="text-right">Azioni</th>
                        </tr>
                    </thead>
                    <tbody>
                        {providers.map((p) => {
                            const isPrepagato = (p.fido_limit || 0) === 0;
                            const pct =
                                p.fido_limit > 0 ? (p.outstanding_credit / p.fido_limit) * 100 : 0;
                            const residuo = p.fido_limit - p.outstanding_credit;
                            return (
                                <tr key={p.id} data-testid={`provider-row-${p.id}`}>
                                    <td className="font-bold">
                                        <div className="flex items-center gap-2">
                                            {p.name}
                                            {isPrepagato && (
                                                <span className="px-2 py-0.5 bg-blue-50 text-blue-700 text-[9px] font-bold uppercase tracking-wider border border-blue-200">
                                                    prepagato
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="text-right tabular-nums">
                                        {isPrepagato ? (
                                            <span className="text-zinc-400">—</span>
                                        ) : (
                                            euro(p.fido_limit)
                                        )}
                                    </td>
                                    <td className="text-right tabular-nums">
                                        {euro(p.outstanding_credit)}
                                    </td>
                                    <td
                                        className="text-right tabular-nums font-bold"
                                        style={{ color: residuo < 0 ? (isPrepagato ? "#002FA7" : "#FF2A00") : "#09090b" }}
                                    >
                                        {euro(isPrepagato ? -p.outstanding_credit : residuo)}
                                    </td>
                                    <td className="w-40">
                                        {isPrepagato ? (
                                            <div className="text-xs text-blue-700">
                                                {p.outstanding_credit < 0
                                                    ? "Saldo disponibile"
                                                    : p.outstanding_credit > 0
                                                      ? "Saldo in uso"
                                                      : "Saldo a zero"}
                                            </div>
                                        ) : (
                                            <>
                                                <div className="h-1 bg-zinc-100 w-full">
                                                    <div
                                                        className="h-full"
                                                        style={{
                                                            width: `${Math.min(100, pct)}%`,
                                                            background: pct > 85 ? "#FF2A00" : "#002FA7",
                                                        }}
                                                    />
                                                </div>
                                                <div className="text-xs text-zinc-500 mt-1 tabular-nums">
                                                    {pct.toFixed(1)}%
                                                </div>
                                            </>
                                        )}
                                    </td>
                                    <td className="text-right">
                                        <div className="flex items-center justify-end gap-2">
                                            <button
                                                onClick={() => setSyncOpen(p)}
                                                className="btn-secondary !py-2 !px-3 flex items-center gap-2"
                                                data-testid={`sync-residuo-${p.id}`}
                                                title="Aggiorna residuo dal portale"
                                            >
                                                <ArrowsClockwise size={14} weight="bold" />
                                                Sync
                                            </button>
                                            {canEdit && (
                                                <>
                                                    <button
                                                        onClick={() => openEdit(p)}
                                                        className="inline-flex items-center justify-center w-9 h-9 text-zinc-500 hover:text-ink hover:bg-zinc-100 transition-colors"
                                                        data-testid={`edit-provider-${p.id}`}
                                                        aria-label="Modifica fornitore"
                                                    >
                                                        <Pencil size={14} weight="bold" />
                                                    </button>
                                                    <button
                                                        onClick={() => setConfirmDel(p)}
                                                        className="inline-flex items-center justify-center w-9 h-9 text-zinc-500 hover:text-red-600 hover:bg-zinc-100 transition-colors"
                                                        data-testid={`delete-provider-${p.id}`}
                                                        aria-label="Elimina fornitore"
                                                    >
                                                        <Trash size={14} weight="bold" />
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                        {providers.length === 0 && (
                            <tr>
                                <td colSpan={6} className="text-center text-zinc-500 py-10">
                                    Nessun fornitore configurato
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="mt-8 p-6 bg-white border border-zinc-200">
                <div className="overline mb-3">Come funziona il flusso cash-centrico</div>
                <ol className="text-sm text-zinc-700 max-w-4xl leading-relaxed space-y-1 list-decimal list-inside">
                    <li>
                        Durante il giorno gli operatori registrano solo i <strong>sospesi</strong>{" "}
                        (dalla pagina Clienti) — non ogni vendita cash.
                    </li>
                    <li>
                        A fine giornata apri il portale del fornitore (SNAI/Eurobet…), leggi il{" "}
                        <strong>residuo</strong> attuale.
                    </li>
                    <li>
                        Clicca <strong>Sync</strong> sulla riga del fornitore e inserisci quel residuo.
                        Il sistema calcola il delta e crea automaticamente la vendita_credito in cassa
                        (o un accredito se il residuo è risalito).
                    </li>
                    <li>
                        Il <strong>rientro fido</strong> settimanale si registra dalla pagina Transazioni.
                    </li>
                </ol>
            </div>

            {open && (
                <div
                    className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-start justify-end"
                    onClick={() => setOpen(false)}
                >
                    <form
                        onClick={(e) => e.stopPropagation()}
                        onSubmit={create}
                        className="w-full max-w-md h-full bg-white border-l border-zinc-200 p-8 overflow-y-auto"
                        data-testid="provider-form"
                    >
                        <div className="overline mb-3">Nuovo Fornitore</div>
                        <h2 className="text-3xl font-black tracking-tighter mb-8">
                            Aggiungi fido.
                        </h2>
                        <label className="block mb-4">
                            <div className="overline mb-2">Nome</div>
                            <input
                                className="inp"
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                required
                                data-testid="provider-form-name"
                            />
                        </label>
                        <label className="block mb-8">
                            <div className="overline mb-2">Limite fido €</div>
                            <MoneyInput
                                className="inp-huge tabular-nums"
                                value={form.fido_limit}
                                onChange={(v) => setForm({ ...form, fido_limit: v })}
                                placeholder="0,00"
                                data-testid="provider-form-fido"
                            />
                            <div className="text-xs text-zinc-500 mt-1">
                                Lascia a 0 se il provider non ti dà fido e lavori{" "}
                                <strong>prepagato</strong> (ricarichi tu il saldo).
                            </div>
                        </label>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                className="btn-secondary flex-1"
                            >
                                Annulla
                            </button>
                            <button
                                type="submit"
                                className="btn-primary flex-1"
                                data-testid="provider-form-submit"
                            >
                                Aggiungi
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {syncOpen && (
                <div
                    className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-start justify-end"
                    onClick={() => setSyncOpen(null)}
                >
                    <form
                        onClick={(e) => e.stopPropagation()}
                        onSubmit={sync}
                        className="w-full max-w-md h-full bg-white border-l border-zinc-200 p-8 overflow-y-auto"
                        data-testid="sync-form"
                    >
                        <div className="overline mb-3">Aggiorna Residuo</div>
                        <h2 className="text-3xl font-black tracking-tighter mb-2">
                            {syncOpen.name}
                        </h2>
                        <div className="text-sm text-zinc-600 mb-8">
                            Fido {euro(syncOpen.fido_limit)} · Residuo attuale{" "}
                            <strong>{euro(syncOpen.fido_limit - syncOpen.outstanding_credit)}</strong>
                        </div>
                        <label className="block mb-4">
                            <div className="overline mb-2">Nuovo residuo dal portale €</div>
                            <MoneyInput
                                className="inp-huge tabular-nums"
                                value={syncForm.residuo}
                                onChange={(v) =>
                                    setSyncForm({ ...syncForm, residuo: v })
                                }
                                required
                                autoFocus
                                placeholder="0,00"
                                data-testid="sync-form-residuo"
                            />
                        </label>
                        <label className="block mb-4">
                            <div className="overline mb-2">Wallet dove accreditare la cassa</div>
                            <select
                                className="inp"
                                value={syncForm.wallet_cash_id}
                                onChange={(e) =>
                                    setSyncForm({ ...syncForm, wallet_cash_id: e.target.value })
                                }
                                data-testid="sync-form-wallet"
                            >
                                <option value="">Auto (prima cassa disponibile)</option>
                                {cashWallets.map((w) => (
                                    <option key={w.id} value={w.id}>
                                        {w.name} ({euro(w.balance)})
                                    </option>
                                ))}
                            </select>
                        </label>
                        <div className="mb-6 p-3 bg-zinc-50 border border-zinc-200 text-xs text-zinc-700">
                            Se il residuo è <strong>sceso</strong> (delta vendite), il sistema
                            registra una <strong>vendita_credito</strong> del delta (cassa sale,
                            outstanding sale). Se è <strong>salito</strong> (vincita pagata al
                            cliente), il sistema registra un <strong>rientro_fido</strong>
                            (cassa scende, outstanding scende).
                        </div>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => setSyncOpen(null)}
                                className="btn-secondary flex-1"
                            >
                                Annulla
                            </button>
                            <button
                                type="submit"
                                className="btn-primary flex-1"
                                data-testid="sync-form-submit"
                            >
                                Aggiorna
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {editOpen && (
                <div
                    className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-start justify-end"
                    onClick={() => setEditOpen(null)}
                >
                    <form
                        onClick={(e) => e.stopPropagation()}
                        onSubmit={saveEdit}
                        className="w-full max-w-md h-full bg-white border-l border-zinc-200 p-8 overflow-y-auto"
                        data-testid="provider-edit-form"
                    >
                        <div className="overline mb-3">Modifica Fornitore</div>
                        <h2 className="text-3xl font-black tracking-tighter mb-8">
                            {editOpen.name}
                        </h2>
                        <label className="block mb-4">
                            <div className="overline mb-2">Nome</div>
                            <input
                                className="inp"
                                value={editForm.name}
                                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                required
                                data-testid="provider-edit-name"
                            />
                        </label>
                        <label className="block mb-4">
                            <div className="overline mb-2">Limite fido €</div>
                            <MoneyInput
                                className="inp-huge tabular-nums"
                                value={editForm.fido_limit}
                                onChange={(v) =>
                                    setEditForm({ ...editForm, fido_limit: v })
                                }
                                placeholder="0,00"
                                data-testid="provider-edit-fido"
                            />
                        </label>
                        <div className="mb-8 p-3 bg-zinc-50 border border-zinc-200 text-xs text-zinc-700">
                            Esposizione attuale: <strong>{euro(editOpen.outstanding_credit)}</strong>.
                            Se riduci il fido sotto l'esposizione, il residuo diventerà negativo
                            fino a un rientro.
                        </div>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => setEditOpen(null)}
                                className="btn-secondary flex-1"
                            >
                                Annulla
                            </button>
                            <button
                                type="submit"
                                className="btn-primary flex-1"
                                data-testid="provider-edit-submit"
                            >
                                Salva
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {confirmDel && (
                <div
                    className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                    onClick={() => setConfirmDel(null)}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="w-full max-w-md bg-white border border-zinc-200 p-8"
                        data-testid="confirm-del-provider"
                    >
                        <div className="overline mb-3">Eliminare fornitore?</div>
                        <h2 className="text-2xl font-black tracking-tighter mb-4">
                            {confirmDel.name}
                        </h2>
                        <p className="text-sm text-zinc-700 mb-6">
                            Fido {euro(confirmDel.fido_limit)} · Esposizione attuale{" "}
                            {euro(confirmDel.outstanding_credit)}. Le transazioni storiche
                            collegate al fornitore restano nel database ma non potrai più
                            registrarne di nuove su questo provider.
                        </p>
                        {confirmDel.outstanding_credit > 0.01 && (
                            <div className="mb-6 p-3 border border-amber-500 text-amber-800 text-xs">
                                Attenzione: c'è ancora un'esposizione di{" "}
                                {euro(confirmDel.outstanding_credit)}. Eliminare il fornitore
                                rimuoverà questo dato dalla quadratura.
                            </div>
                        )}
                        <div className="flex gap-2">
                            <button
                                onClick={() => setConfirmDel(null)}
                                className="btn-secondary flex-1"
                            >
                                Annulla
                            </button>
                            <button
                                onClick={remove}
                                className="btn-destructive flex-1"
                                data-testid="confirm-del-provider-btn"
                            >
                                Elimina
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
